<script>
import { watchEffect } from 'vue';

export default {
  data() {
    return {
      selectedCategoryName: '',
      selectedPreviewImage: '',
      customBackgrounds: [],
      setWebsiteWatch: null,
      hasImageSizeWarning: false,
      hasImageDimensionWarning: false,
    };
  },
  inject: {
    $giftCardStateReactive: '$giftCardStateReactive',
    $websiteImagesReactive: '$websiteImagesReactive',
    changeWebsiteImage: 'changeWebsiteImage',
    $imageLimits: '$imageLimits',
  },
  emits: ['deleteWebsiteImage', 'upload-images'],
  computed: {
    categories() {
      return this.$websiteImagesReactive;
    },
    backgrounds() {
      const covers = this.categories[
        this.categories.map((c) => c.name).indexOf(this.currentCategory?.name)
      ]?.covers;

      return covers;
    },
    currentCategory() {
      return (
        this.categories.find((c) => c.name === this.selectedCategoryName)
        || this.categories?.[0]
        || {}
      );
    },
    currentPreviewImage() {
      return (
        this.selectedPreviewImage || this.currentCategory?.covers?.[0] || ''
      );
    },
    showUpload() {
      return this.currentCategory?.value?.toLowerCase() === 'custom';
    },
    uploadSizeWarning() {
      return this.$replaceTokens(this.$globalTexts.giftcard__image_too_large, {
        size: `${this.$imageLimits.websiteImageSizeLimit}MB`,
      }) || `Use an image that is smaller than ${this.$imageLimits.websiteImageSizeLimit}MB`;
    },
    uploadDimensionWarning() {
      return this.$replaceTokens(this.$globalTexts.giftcard__image_aspect_ratio, {
        ratio: `${this.$imageLimits.websiteImageAspectRatioLimit}`,
      }) || `Use an image with a ${this.$imageLimits.websiteImageAspectRatioLimit} aspect ratio`;
    },
  },
  methods: {
    setSelectedCategory(categoryName) {
      this.selectedPreviewImage = '';
      this.selectedCategoryName = categoryName;
    },
    setSelectedPreviewImage(cover) {
      this.selectedPreviewImage = cover;
      this.changeWebsiteImage(cover);
    },
    checkSelectedCategory(categoryName) {
      return this.currentCategory?.name === categoryName ? '' : undefined;
    },
    getBackgroundImage(imageId) {
      return this.$toLitiumMediaUrl(imageId, { maxWidth: 135 });
    },
    handleImage(e) {
      const images = e.target.files || [];
      this.hasImageSizeWarning = false;
      this.hasImageDimensionWarning = false;
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < images.length; i++) {
        this.convertToBase64Image(images[i], i === images.length - 1);
      }
    },
    convertToBase64Image(fileImage, isLast) {
      const imgSize = ((fileImage.size / 1024) / 1024).toFixed(5);

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const imageDimensions = img.width / img.height;
          let currentUploadSizeWarning = false;
          let currentUploadDimensionWarning = false;

          if (imgSize > this.$imageLimits.websiteImageSizeLimit) {
            this.hasImageSizeWarning = true;
            currentUploadSizeWarning = true;
          }

          if (imageDimensions < this.$imageLimits.websiteImageAspectRatioLimit) {
            this.hasImageDimensionWarning = true;
            currentUploadDimensionWarning = true;
          }

          if (!currentUploadSizeWarning && !currentUploadDimensionWarning) {
            this.customBackgrounds.push({
              name: fileImage.name,
              type: fileImage.type,
              src: img.src,
            });
          }

          if (isLast && this.customBackgrounds.length > 0) {
            this.$emit('upload-images', [...this.customBackgrounds]);
            this.customBackgrounds = [];
          }
        };
      };
      reader.readAsDataURL(fileImage);
    },
    isImageSelected(cover) {
      return (
        this.currentPreviewImage
        && this.currentPreviewImage.name === cover.name
        && this.currentPreviewImage.id
          === this.$giftCardStateReactive.websiteImageSystemId
      );
    },
    removeSelected() {
      this.$emit('deleteWebsiteImage', this.currentPreviewImage);
    },
    setDefaultSelectedImage() {
      this.selectedPreviewImage = '';
      if (this.currentPreviewImage) {
        this.setSelectedPreviewImage(this.currentPreviewImage);
      } else {
        this.selectedCategoryName = '';
        this.setSelectedCategory(this.currentCategory.name);
        this.setSelectedPreviewImage(this.currentPreviewImage);
      }
    },
  },
  mounted() {
    this.setWebsiteWatch = watchEffect(() => {
      if (this.currentPreviewImage) {
        if (this.$giftCardStateReactive?.websiteImageSystemId) {
          const category = this.categories.find((c) => (c.covers || []).find(
            (s) => s.id === this.$giftCardStateReactive.websiteImageSystemId,
          ));

          if (category) {
            this.setSelectedCategory(category.name);

            const image = (category.covers || []).find(
              (s) => s.id === this.$giftCardStateReactive.websiteImageSystemId,
            );
            if (image) {
              this.setSelectedPreviewImage(image);
            }
          }
        }
        if (this.setWebsiteWatch) {
          this.setWebsiteWatch();
        }
      }
    });

    this.$mitt.on(
      'giftCardEditor:websiteImagesUploaded',
      this.setSelectedPreviewImage,
    );
    this.$mitt.on(
      'giftCardEditor:websiteImageDeleted',
      this.setDefaultSelectedImage,
    );
  },
  beforeUnmount() {
    this.$mitt.off(
      'giftCardEditor:websiteImagesUploaded',
      this.setSelectedPreviewImage,
    );
    this.$mitt.off(
      'giftCardEditor:websiteImageDeleted',
      this.setDefaultSelectedImage,
    );

    if (this.setWebsiteWatch) {
      this.setWebsiteWatch();
    }
  },
};
</script>

<template>
  <div class="web-menu-background">
    <div v-if="categories.length > 1" class="web-menu-background__select">
      <label>
        {{ $globalTexts.category_facets_category || 'category_facets_category' }}
        <select @change="setSelectedCategory($event.currentTarget.value)">
          <option
            v-for="category in categories"
            :key="category.name"
            :selected="checkSelectedCategory(category.name)"
            :value="category.name"
          >
            {{ category.name }}
          </option>
        </select>
      </label>
    </div>
    <template v-if="showUpload">
      <label
        for="web-menu-background__file-upload"
        class="secondary-button web-menu-background__upload-label"
      >
        <input
          type="file"
          id="web-menu-background__file-upload"
          class="web-menu-background__upload-button"
          accept="image/*"
          multiple="true"
          @change="handleImage"
        />
        <img src="../../../static/icons/upload_icon.svg" alt="upload-icon" />
        {{ $globalTexts.giftcard__upload_image_text || 'giftcard__upload_image_text' }}
      </label>
      <p class="web-menu-background__upload-suggestion">
        {{ $globalTexts.giftcard__background_size_recommendation || 'giftcard__background_size_recommendation' }}
      </p>
      <p
        class="web-menu-background__upload-filename"
        v-if="backgrounds.length == 0 && !hasImageSizeWarning && !hasImageDimensionWarning"
      >
        {{ $globalTexts.giftcard__no_file || 'giftcard__no_file' }}
      </p>
      <div
        class="web-menu-background__upload-warning"
        v-if="hasImageSizeWarning || hasImageDimensionWarning"
      >
        <p v-if="hasImageSizeWarning">
          {{ uploadSizeWarning }}
        </p>
        <p v-if="hasImageDimensionWarning">
          {{ uploadDimensionWarning }}
        </p>
      </div>
    </template>
    <div class="web-menu-background__background-list">
      <div
        v-for="bg in backgrounds"
        :key="bg.name"
        :class="[
          'web-menu-background__background',
          {
            'web-menu-background__background--selected': isImageSelected(bg),
          },
        ]"
        @click="setSelectedPreviewImage(bg)"
      >
        <div class="web-menu-background__background-spacer"></div>
        <img
          class="web-menu-background__background-image"
          :src="getBackgroundImage(bg.id)"
          :alt="bg.name"
        />
      </div>
    </div>
    <button
      v-if="currentPreviewImage && showUpload"
      class="secondary-button web-menu-background__remove-background"
      @click="removeSelected"
    >
      <img
        class="web-menu-background__remove-icon"
        src="../../../static/icons/trash_icon.svg"
        alt="remove-icon"
      />
      {{ $globalTexts.giftcard__remove_selected || 'giftcard__remove_selected' }}
    </button>
  </div>
</template>

<style>
.web-menu-background {
  display: flex;
  flex-direction: column;
}

.web-menu-background__background-list {
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.web-menu-background__background {
  position: relative;
}

.web-menu-background__background--selected::after {
  box-shadow: inset 0 0 0 1px #000;
  content: "";
  position: absolute;
  inset: 0;
}

.web-menu-background__background-spacer {
  padding-top: 35%;
  background: var(--color-neutrals-45);
}

.web-menu-background__background-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-position: center;
  object-fit: cover;
}

.web-menu-background__select {
  text-transform: none;
}

label.web-menu-background__upload-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 26px;
}

.web-menu-background__upload-button {
  display: none;
}

.web-menu-background__upload-suggestion {
  text-align: center;
  font-size: 12px;
  line-height: 14px;
  margin-bottom: 0;
}

.web-menu-background__upload-filename {
  text-align: center;
  font-size: 12px;
  line-height: 14px;
  margin: 17px 0;
  font-style: italic;
  color: var(--color-neutrals-30);
}

.web-menu-background__upload-warning {
  font-size: 14px;
  line-height: 24px;
  padding: 6px;
  margin: 10px 0;
  text-align: center;
  background: #FF5042;
  border-radius: 5px;
  color: white;
}

.web-menu-background__upload-warning p{
  margin: 0;
}

button.web-menu-background__remove-background {
  margin: 1rem auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: fit-content;
}

.web-menu-background__remove-icon {
  filter: brightness(0) saturate(100%);
}
</style>
